import { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import '@mdxeditor/editor/style.css';
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  ListsToggle,
  CodeToggle,
  InsertThematicBreak,
  Separator,
  UndoRedo,
} from '@mdxeditor/editor';

const MarkdownEditor = ({
  value,
  onChange,
  name,
  label,
  inputRef: externalRef,
  error,
  helperText,
}) => {
  const editorRef = useRef(null);
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (externalRef) {
      externalRef.current = {
        focus: () => editorRef.current?.focus(),
      };
    }
  }, [externalRef]);

  useEffect(() => {
    if (value !== lastValueRef.current && editorRef.current) {
      editorRef.current.setMarkdown(value || '');
    }
    lastValueRef.current = value;
  }, [value]);

  const handleChange = (markdown) => {
    const cleaned = markdown.replace(/&#x20;/g, ' ');
    lastValueRef.current = cleaned;
    onChange({ target: { name, value: cleaned } });
  };

  return (
    <Box sx={{ mt: 2 }}>
      {label && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mb: 0.5, display: 'block', ml: 0.5 }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: 1,
          borderColor: error
            ? 'error.main'
            : (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
          borderRadius: 1,
          overflow: 'hidden',
          '&:hover': {
            borderColor: error
              ? 'error.main'
              : (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)',
          },
          '&:focus-within': {
            borderColor: error ? 'error.main' : 'primary.main',
            boxShadow: (theme) => error
              ? `0 0 0 1px ${theme.palette.error.main}`
              : `0 0 0 1px ${theme.palette.primary.main}`,
          },
          '& .mdxeditor-content': {
            minHeight: '40vh',
            fontFamily: 'inherit',
            padding: '12px 14px',
          },
          '& .mdxeditor-toolbar': {
            borderRadius: 0,
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
          },
        }}
      >
        <MDXEditor
          ref={editorRef}
          markdown={value || ''}
          onChange={handleChange}
          contentEditableClassName="mdxeditor-content"
          plugins={[
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            thematicBreakPlugin(),
            markdownShortcutPlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  <UndoRedo />
                  <Separator />
                  <BoldItalicUnderlineToggles />
                  <Separator />
                  <BlockTypeSelect />
                  <Separator />
                  <ListsToggle />
                  <Separator />
                  <CodeToggle />
                  <InsertThematicBreak />
                </>
              ),
            }),
          ]}
        />
      </Box>
      {error && helperText && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, ml: 1.75, display: 'block' }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default MarkdownEditor;
